import { useState } from "react";
import axios from "axios";
import { FileText, Loader, ArrowRight, Shield, BarChart3, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { API, getErrorMessage } from "../lib/api";

export default function AuthView({ onLogin }: {
  authView?: "login" | "register";
  setAuthView?: (v: "login" | "register") => void;
  onLogin: (token: string, user: any, isNewUser: boolean) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: "", password: "", full_name: "", company_name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");

  const handleSubmit = async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      params.append("username", form.email);
      params.append("password", form.password);
      if (mfaCode) params.append("mfa_code", mfaCode);
      const res = await axios.post(`${API}/auth/login`, params, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" }
      });
      if (res.data.refresh_token) localStorage.setItem("contrack_refresh_token", res.data.refresh_token);
      onLogin(res.data.access_token, res.data.user, !!res.data.user?.needs_onboarding);
    } catch (e: any) {
      // Sjekk om MFA-kode er påkrevd
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && (detail.error === "mfa_required" || detail.error === "mfa_invalid")) {
        setMfaRequired(true);
        if (detail.error === "mfa_invalid") {
          setError(t("auth.mfa_invalid"));
        }
      } else {
        setError(getErrorMessage(e, t("auth.noe_gikk_galt")));
      }
    } finally { setLoading(false); }
  };

  const inp = "w-full bg-canvas border border-line rounded-xl px-4 py-3.5 text-[14px] text-night placeholder-faint transition-all";

  return (
    <div className="min-h-screen bg-night flex">
      {/* Venstre — branding */}
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-night" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-white/[0.06] rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-0 w-64 h-64 bg-white/[0.04] rounded-full blur-3xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-20">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center shadow-lg shadow-black/20">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-white tracking-tight">Contrack</span>
          </div>
          <h2 className="text-4xl font-bold text-white leading-[1.15] mb-6">
            {t("auth.hero_title_1")}<br />{t("auth.hero_title_2")}<br />
            <span className="text-white/70">{t("auth.hero_title_3")}</span>
          </h2>
          <p className="text-[15px] text-white/60 max-w-sm leading-relaxed">
            {t("auth.hero_sub")}
          </p>
        </div>
        <div className="relative z-10 space-y-3">
          {[
            { icon: <Shield className="w-4 h-4" />, text: t("auth.feature_avvik") },
            { icon: <Mail className="w-4 h-4" />, text: t("auth.feature_email") },
            { icon: <BarChart3 className="w-4 h-4" />, text: t("auth.feature_fidic") },
          ].map((f) => (
            <div key={f.text} className="flex items-center gap-3 text-[13px] text-white/60">
              <span className="text-white/70">{f.icon}</span>
              {f.text}
            </div>
          ))}
        </div>
      </div>

      {/* Hoyre — skjema */}
      <div className="flex-1 flex items-center justify-center p-8 bg-canvas">
        <div className="w-full max-w-[420px]">
          <div className="text-center mb-8 lg:hidden">
            <div className="w-12 h-12 bg-night rounded-[14px] flex items-center justify-center mx-auto mb-4 shadow-lg shadow-night/20">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-night">Contrack</h1>
          </div>

          <div className="bg-white rounded-[14px] border border-line shadow-kpi p-8">
            <h2 className="text-xl font-bold text-night mb-1">{t("auth.velkommen_tilbake")}</h2>
            <p className="text-[13px] text-muted mb-6">{t("auth.logg_inn_konto")}</p>

            <div className="space-y-4">
              <div>
                <label className="text-[12px] font-medium text-muted mb-1.5 block">{t("auth.epost")}</label>
                <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="ola@selskap.no" type="email" className={inp} />
              </div>
              <div>
                <label className="text-[12px] font-medium text-muted mb-1.5 block">{t("auth.passord")}</label>
                <input value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                  placeholder={t("auth.passord_placeholder_login")}
                  type="password" onKeyDown={e => e.key === "Enter" && handleSubmit()} className={inp} />
              </div>
              {mfaRequired && (
                <div>
                  <label className="text-[12px] font-medium text-muted mb-1.5 block">{t("auth.mfa_code")}</label>
                  <input value={mfaCode} onChange={e => setMfaCode(e.target.value.replace(/\s/g, "").toUpperCase())}
                    placeholder="123456"
                    autoFocus
                    onKeyDown={e => e.key === "Enter" && handleSubmit()}
                    className={inp + " tabular"} />
                  <p className="text-[11px] text-faint mt-1.5">{t("auth.mfa_hint")}</p>
                </div>
              )}
              {error && <div className="bg-neg/10 border border-neg/20 rounded-xl p-3 text-[13px] text-neg">{error}</div>}
              <button onClick={handleSubmit} disabled={loading}
                className="w-full py-3.5 rounded-xl text-[14px] font-semibold bg-night text-white hover:bg-night/90 disabled:bg-line disabled:text-faint flex items-center justify-center gap-2 transition-all mt-2">
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <>{mfaRequired ? t("auth.verify_mfa") : t("auth.logg_inn")}<ArrowRight className="w-4 h-4" /></>}
              </button>
              {(
                <div className="text-center pt-1">
                  <button onClick={() => { setShowForgot(!showForgot); setForgotSent(false); }} className="text-[12px] text-faint hover:text-night font-medium transition-colors">{t("auth.glemt_passord")}</button>
                  {showForgot && (
                    <div className="mt-3 bg-canvas border border-line rounded-xl p-4">
                      {forgotSent ? (
                        <p className="text-[12px] text-pos font-medium">{t("auth.sjekk_epost")}</p>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-[12px] text-muted">{t("auth.skriv_epost_reset")}</p>
                          <input value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                            placeholder={t("auth.epostadresse")} type="email"
                            className="w-full bg-white border border-line rounded-xl px-4 py-2.5 text-[13px]" />
                          <button disabled={forgotLoading || !forgotEmail} onClick={async () => {
                            setForgotLoading(true);
                            try {
                              await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
                              setForgotSent(true);
                            } catch { setError(t("auth.kunne_ikke_sende")); }
                            finally { setForgotLoading(false); }
                          }} className="w-full py-2.5 rounded-xl text-[13px] font-semibold bg-night text-white hover:bg-night/90 disabled:bg-line disabled:text-faint transition-all">
                            {forgotLoading ? t("auth.sender") : t("auth.send_reset_lenke")}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
